import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { FileDown, Pencil, PencilLine, Trash2, History } from "lucide-react";
import api, { errMsg, download } from "@/lib/api";
import { fdate, money } from "@/lib/fmt";
import { Button } from "@/components/ui/button";
import { PreviewButton } from "@/components/PdfPreview";
import { ProformaForm } from "@/components/proforma/ProformaForm";

const fname = (d) => `PROFORMA-${(d.bill_to?.name || "").replace(/[^A-Za-z0-9]+/g, "-")}-${d.display_no}.pdf`;

const Versions = ({ d }) => {
  const [rows, setRows] = useState(null);
  if (!d.rev_no) return null;
  return (
    <div className="mt-2">
      <button type="button" className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline" onClick={() => api.get(`/proforma/${d.id}/versions`).then((r) => setRows(rows ? null : r.data))} data-testid={`pi-versions-${d.id}`}><History className="h-3 w-3" />Riwayat revisi</button>
      {rows && (
        <ul className="mt-1 space-y-1 text-xs text-slate-600">
          {rows.map((v) => (
            <li key={v.id} className="flex items-center justify-between gap-2" data-testid={`pi-version-${v.id}`}>
              <span><b className="font-num">{v.display_no}</b> • {v.bill_to?.name}</span>
              <PreviewButton icon url={`/proforma/${v.id}/pdf`} filename={fname(v)} title={v.display_no} testId={`pi-version-preview-${v.id}`} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default function Proforma() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(null);
  const [sp, setSp] = useSearchParams();
  const load = () => api.get("/proforma").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const id = sp.get("id");
    if (id) api.get(`/proforma/${id}`).then((r) => { setForm({ doc: r.data, mode: "edit" }); setSp({}); });
  }, [sp]); // eslint-disable-line react-hooks/exhaustive-deps
  const del = async (d) => {
    if (!window.confirm(`Hapus ${d.display_no}?`)) return;
    try { await api.delete(`/proforma/${d.id}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-6" data-testid="proforma-page">
      <div>
        <h1 className="font-heading text-4xl font-bold tracking-tight sm:text-5xl">Proforma Invoice</h1>
        <p className="mt-2 text-sm text-slate-500">Dibuat dari penawaran Deal / Confirmed PO. Buka penawaran lalu klik <b>Buat Proforma</b>.</p>
      </div>
      {!rows.length && <p className="rounded-xl border border-dashed p-8 text-center text-sm text-slate-400" data-testid="proforma-empty">Belum ada Proforma Invoice.</p>}
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {rows.map((d) => (
          <div key={d.id} className="card-premium p-4" data-testid={`pi-card-${d.id}`}>
            <div className="flex items-center justify-between gap-2">
              <p className="font-num text-xs font-semibold text-blue-900" data-testid={`pi-no-${d.id}`}>{d.display_no}</p>
              {d.rev_no > 0 && <span className="rounded bg-rose-100 px-2 py-0.5 text-[10px] font-bold uppercase text-rose-700">Rev {d.rev_no}</span>}
            </div>
            <p className="mt-1 font-semibold" data-testid={`pi-billto-${d.id}`}>{d.bill_to?.name || "-"}</p>
            <p className="text-xs text-slate-500">Penawaran {d.quotation_no || "-"} • PO {d.po_no || "-"}</p>
            <p className="mt-1 font-num text-sm font-semibold" data-testid={`pi-total-${d.id}`}>{money(d.total, d.currency)}</p>
            <p className="text-xs text-slate-400">{fdate(d.date)} • jatuh tempo {fdate(d.due_date)}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <PreviewButton size="sm" url={`/proforma/${d.id}/pdf`} filename={fname(d)} title={d.display_no} testId={`pi-preview-${d.id}`} />
              <Button size="sm" onClick={() => download(`/proforma/${d.id}/pdf`, {}, fname(d)).catch((e) => toast.error(errMsg(e)))} data-testid={`pi-pdf-${d.id}`}><FileDown className="h-4 w-4 mr-1" />PDF</Button>
              <Button size="sm" variant="outline" onClick={() => setForm({ doc: d, mode: "edit" })} data-testid={`pi-edit-${d.id}`}><Pencil className="h-4 w-4 mr-1" />Edit</Button>
              <Button size="sm" variant="outline" onClick={() => setForm({ doc: d, mode: "revise" })} data-testid={`pi-revise-${d.id}`}><PencilLine className="h-4 w-4 mr-1" />Revisi</Button>
              <Button size="icon" variant="ghost" className="text-rose-500" onClick={() => del(d)} data-testid={`pi-delete-${d.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
            <Versions d={d} />
          </div>
        ))}
      </div>
      <ProformaForm form={form} onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />
    </div>
  );
}
